import { useTranslation } from 'react-i18next'
import { UnitLabel } from '@/shared/config/config'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  DataBlock,
  NumericInputGroup,
  isValidPercentage
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import calculateProcessEmissionsFromSoderbergCell from '@/features/reports/formulas/calculateProcessEmissionsFromSoderbergCell'
import { Artifacts } from '@/api/EmissionReportModel'
import { useEffect } from 'react'
import { useState } from 'react'
import { useCalculatorValidation } from '@/hooks/useCalculatorValidation'
import { usePermissions } from '@/shared/hooks/usePermissions'

export default function AluminiumSoderbergAnodeCalculatorPanel({
  isOpen,
  close,
  aluminiumQuantity,
  initialValues,
  addEmission,
  assetDefinitionId
}: {
  isOpen: boolean
  close: () => void
  aluminiumQuantity: number
  initialValues: Artifacts
  addEmission: (emission: number, artifacts: Artifacts) => void
  assetDefinitionId?: string | null
}) {
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  const { useInputOutsideRangeValidation: binderContentInPasteValidation } =
    useCalculatorValidation({
      minimumValue: 18,
      maximumValue: 33.75,
      unit: UnitLabel.wtPerCent
    })

  const { useInputOutsideRangeValidation: sulphurContentInPitchValidation } =
    useCalculatorValidation({
      minimumValue: 0.48,
      maximumValue: 0.72,
      unit: UnitLabel.wtPerCent
    })
  const { useInputOutsideRangeValidation: ashContentInPitchValidation } = useCalculatorValidation({
    minimumValue: 0.16,
    maximumValue: 0.24,
    unit: UnitLabel.wtPerCent
  })

  const { useInputOutsideRangeValidation: hydrogenContentInPitchValidation } =
    useCalculatorValidation({
      minimumValue: 1.65,
      maximumValue: 4.95,
      unit: UnitLabel.wtPerCent
    })

  const { useInputOutsideRangeValidation: sulphurContentInCalcinedCokeValidation } =
    useCalculatorValidation({
      minimumValue: 1.52,
      maximumValue: 2.28,
      unit: UnitLabel.wtPerCent
    })

  const { useInputOutsideRangeValidation: ashContentInCalcinedCokeValidation } =
    useCalculatorValidation({
      minimumValue: 0.1,
      maximumValue: 0.3,
      unit: UnitLabel.wtPerCent
    })

  const { useInputAboveMaximumValueValidation: carbonInSkimmedDustFromSoderbergCellValidation } =
    useCalculatorValidation({
      maximumValue: 0.0199,
      unit: UnitLabel.tPerTAl
    })

  const cyclohexaneSolubleMatterEmissionsValidationParams = {
    minimumValue: assetDefinitionId === 'horizontal_stud_soderberg' ? 2.8 : 0.35,
    maximumValue: assetDefinitionId === 'horizontal_stud_soderberg' ? 5.2 : 0.65,
    unit: UnitLabel.kgPerTAl,
    assetName: t(`assets:${assetDefinitionId}`)
  }

  const { useInputIsOutOfRangeForSpecificAsset: cyclohexaneSolubleMatterEmissionsValidation } =
    useCalculatorValidation(cyclohexaneSolubleMatterEmissionsValidationParams)

  const validations = {
    binderContentInPaste: binderContentInPasteValidation,
    sulphurContentInPitch: sulphurContentInPitchValidation,
    ashContentInPitch: ashContentInPitchValidation,
    hydrogenContentInPitch: hydrogenContentInPitchValidation,
    sulphurContentInCalcinedCoke: sulphurContentInCalcinedCokeValidation,
    ashContentInCalcinedCoke: ashContentInCalcinedCokeValidation,
    carbonInSkimmedDustFromSoderbergCell: carbonInSkimmedDustFromSoderbergCellValidation,
    cyclohexaneSolubleMatterEmissions: cyclohexaneSolubleMatterEmissionsValidation
  }

  const [state, setState] = useState({
    pasteConsumption: initialValues?.pasteConsumption || '', // t/t Al
    cyclohexaneSolubleMatterEmissions: initialValues?.cyclohexaneSolubleMatterEmissions || '', // kg/t Al
    binderContentInPaste: initialValues?.binderContentInPaste || '', // wt %
    sulphurContentInPitch: initialValues?.sulphurContentInPitch || '', // wt %
    ashContentInPitch: initialValues?.ashContentInPitch || '', // wt %
    hydrogenContentInPitch: initialValues?.hydrogenContentInPitch || '', // wt %
    sulphurContentInCalcinedCoke: initialValues?.sulphurContentInCalcinedCoke || '', // wt %
    ashContentInCalcinedCoke: initialValues?.ashContentInCalcinedCoke || '', // wt %
    carbonInSkimmedDustFromSoderbergCell: initialValues?.carbonInSkimmedDustFromSoderbergCell || '', // t C/t Al
    aluminiumQuantity // t Al/t Al
  })

  const onValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const name = evt.target.name
    const value = Number(evt.target.value)
    const validation = validations[name as keyof typeof validations]
    const validationData = validation?.safeParse(value)

    if (validation && !validationData.success && value > 0) {
      setWarnings(prev => ({ ...prev, [name]: validationData.error?.issues[0].message }))
    } else {
      setWarnings(prev => ({ ...prev, [name]: '' }))
    }
    setState(prev => ({ ...prev, [evt.target.name]: evt.target.value }))
  }

  const emission = calculateProcessEmissionsFromSoderbergCell({
    pasteConsumption: Number(state.pasteConsumption),
    cyclohexaneSolubleMatterEmissions: Number(state.cyclohexaneSolubleMatterEmissions),
    binderContentInPaste: Number(state.binderContentInPaste),
    sulphurContentInPitch: Number(state.sulphurContentInPitch),
    ashContentInPitch: Number(state.ashContentInPitch),
    hydrogenContentInPitch: Number(state.hydrogenContentInPitch),
    sulphurContentInCalcinedCoke: Number(state.sulphurContentInCalcinedCoke),
    ashContentInCalcinedCoke: Number(state.ashContentInCalcinedCoke),
    carbonInSkimmedDustFromSoderbergCell: Number(state.carbonInSkimmedDustFromSoderbergCell),
    aluminiumQuantity
  })

  const isValid = emission > 0

  const onAddClick = () => {
    if (!isValid) {
      return
    }
    const artifacts = state
    addEmission(emission, artifacts)
    close()
  }

  useEffect(() => {
    if (isOpen && initialValues) {
      setState(initialValues)
    }
  }, [isOpen, initialValues])

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card className="relative">
        <CardHeader>
          <CardTitle title={t('report:calculators.aluminiumSoderbergCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="col-span-2">
              <NumericInputGroup
                name="pasteConsumption"
                label={t(
                  'report:calculators.aluminiumSoderbergCalculatorPanel.fields.pasteConsumption.label'
                )}
                suffix={UnitLabel.tPerTAl}
                value={state.pasteConsumption}
                onChange={e => setState(prev => ({ ...prev, pasteConsumption: e.target.value }))}
                numericFormat={{
                  decimalScale: 3,
                  allowNegative: false
                }}
              />
            </div>
            <NumericInputGroup
              name="cyclohexaneSolubleMatterEmissions"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.cyclohexaneSolubleMatterEmissions.label'
              )}
              suffix={UnitLabel.kgPerTAl}
              value={state.cyclohexaneSolubleMatterEmissions}
              warning={warnings.cyclohexaneSolubleMatterEmissions}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
            />
            <NumericInputGroup
              name="binderContentInPaste"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.binderContentInPaste.label'
              )}
              value={state.binderContentInPaste}
              warning={warnings.binderContentInPaste}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="sulphurContentInPitch"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.sulphurContentInPitch.label'
              )}
              value={state.sulphurContentInPitch}
              warning={warnings.sulphurContentInPitch}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false,
                isAllowed: isValidPercentage
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="ashContentInPitch"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.ashContentInPitch.label'
              )}
              value={state.ashContentInPitch}
              warning={warnings.ashContentInPitch}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false,
                isAllowed: isValidPercentage
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="hydrogenContentInPitch"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.hydrogenContentInPitch.label'
              )}
              value={state.hydrogenContentInPitch}
              warning={warnings.hydrogenContentInPitch}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false,
                isAllowed: isValidPercentage
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="sulphurContentInCalcinedCoke"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.sulphurContentInCalcinedCoke.label'
              )}
              value={state.sulphurContentInCalcinedCoke}
              warning={warnings.sulphurContentInCalcinedCoke}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false,
                isAllowed: isValidPercentage
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="ashContentInCalcinedCoke"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.ashContentInCalcinedCoke.label'
              )}
              value={state.ashContentInCalcinedCoke}
              warning={warnings.ashContentInCalcinedCoke}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false,
                isAllowed: isValidPercentage
              }}
              suffix={UnitLabel.wtPerCent}
            />
            <NumericInputGroup
              name="carbonInSkimmedDustFromSoderbergCell"
              label={t(
                'report:calculators.aluminiumSoderbergCalculatorPanel.fields.carbonInSkimmedDustFromSoderbergCell.label'
              )}
              value={state.carbonInSkimmedDustFromSoderbergCell}
              warning={warnings.carbonInSkimmedDustFromSoderbergCell}
              onChange={onValueChange}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
              suffix={UnitLabel.tPerTAl}
            />
          </div>
          <DataBlock label={t('report:common:emission')} data={`${emission} ${UnitLabel.tCO2}`} />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 px-4 pb-4">
          <Button onClick={close} variant="subtle">
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!isValid || !canEditReport} onClick={onAddClick}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
